import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TopBar } from "../components/UI";

const API = process.env.REACT_APP_API_URL || "https://g6fw4rjkvb.execute-api.eu-central-1.amazonaws.com/prototype";

// Reached from the "Forgot password?" link on every login screen, which
// passes whatever was typed in its email field via router state (kept out of
// the URL on purpose). Pressing "Reset password" asks the API to email a
// time-limited link (see auth/handler.py::request_password_reset).
const ROLE_LABELS = {
  winery_owner: "Winery Manager",
  traveler: "Traveler",
  road_rep: "Wine Road Representative",
  admin: "Platform Administrator",
};

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || "");
  // The reset applies to exactly one account: the role of the login screen
  // the user came from. If the page is opened directly (no role passed),
  // the user picks it themselves.
  const roleFromLogin = ROLE_LABELS[location.state?.role] ? location.state.role : "";
  const [role, setRole] = useState(roleFromLogin);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resent, setResent] = useState(false);
  // Seconds until "Resend email" is enabled. Matches the API's
  // RESET_REQUEST_COOLDOWN_SECONDS (60): a request inside that window is
  // silently ignored server-side, so don't let the user press it earlier.
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (isResend = false) => {
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), role }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Something went wrong. Please try again.");
      setSent(true);
      setResent(isResend);
      setCooldown(60);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const labelStyle = { fontSize: 10, fontWeight: 600, color: "#6A7E58", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 4 };
  const inputStyle = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #C4D0A8", background: "#EDF1E4", color: "#2C3820", fontSize: 13, boxSizing: "border-box" };
  const canSubmit = !loading && email.includes("@") && !!role;

  return (
    <div style={{ minHeight: "100vh", background: "#F2F5EC" }}>
      <TopBar title="Wine4Collect" back={() => navigate(-1)} />
      <div style={{ maxWidth: 420, margin: "60px auto", padding: "0 16px" }}>
        <div style={{ background: "white", borderRadius: 12, padding: 32, border: "1px solid #C4D0A8" }}>
          {sent ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>✉️</div>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: "#3D4A1A", margin: "0 0 8px" }}>Check your email</h2>
              <p style={{ fontSize: 13, color: "#6A7E58", lineHeight: 1.5 }}>
                If a <strong>{ROLE_LABELS[role]}</strong> account exists for <strong>{email.trim()}</strong>, we've sent a link to reset its password.
                It is valid for 2 hours and can be used once.
              </p>
              <p style={{ fontSize: 12, color: "#6A7E58", lineHeight: 1.5 }}>
                Didn't get it? Check your spam folder, or request another email below. A new link replaces the previous one.
              </p>
              {error && <div style={{ background: "rgba(139,26,47,0.08)", color: "#8B1A2F", padding: "10px 12px", borderRadius: 8, fontSize: 13, margin: "12px 0" }}>{error}</div>}
              {resent && !error && cooldown > 0 && (
                <div style={{ background: "rgba(61,74,26,0.08)", color: "#3D4A1A", padding: "10px 12px", borderRadius: 8, fontSize: 13, margin: "12px 0" }}>Email sent again.</div>
              )}
              <button onClick={() => submit(true)} disabled={loading || cooldown > 0}
                style={{ marginTop: 12, width: "100%", padding: "11px", borderRadius: 8, border: "none", background: "#3D4A1A", color: "white", fontWeight: 600, fontSize: 14, cursor: "pointer", opacity: (loading || cooldown > 0) ? 0.6 : 1 }}>
                {loading ? "Sending…" : cooldown > 0 ? `Resend email (${cooldown}s)` : "Resend email"}
              </button>
              <button onClick={() => { setSent(false); setResent(false); setError(""); setCooldown(0); }}
                style={{ marginTop: 10, background: "none", border: "none", color: "#6A7E58", fontSize: 12, textDecoration: "underline", cursor: "pointer" }}>
                Use a different email
              </button>
              <br />
              <button onClick={() => navigate("/")}
                style={{ marginTop: 16, padding: "10px 20px", borderRadius: 8, border: "1px solid #C4D0A8", background: "white", color: "#3D4A1A", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                Back to home
              </button>
            </div>
          ) : (
            <>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: "#1E2C14", margin: "0 0 8px" }}>Reset your password</h2>
              <p style={{ fontSize: 13, color: "#6A7E58", marginBottom: 24 }}>
                {roleFromLogin
                  ? <>Enter the email of your <strong>{ROLE_LABELS[roleFromLogin]}</strong> account and we'll send you a link to choose a new password.</>
                  : "Choose your account type and enter its email — we'll send you a link to choose a new password."}
              </p>
              {error && <div style={{ background: "rgba(139,26,47,0.08)", color: "#8B1A2F", padding: "10px 12px", borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
              {!roleFromLogin && (
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Account type</label>
                  <select value={role} onChange={e => setRole(e.target.value)} style={inputStyle}>
                    <option value="">Select…</option>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              )}
              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && canSubmit && submit(false)}
                  placeholder="you@example.com" style={inputStyle} />
              </div>
              <button onClick={() => submit(false)} disabled={!canSubmit}
                style={{ width: "100%", padding: "11px", borderRadius: 8, border: "none", background: "#3D4A1A", color: "white", fontWeight: 600, fontSize: 14, cursor: "pointer", opacity: canSubmit ? 1 : 0.6 }}>
                {loading ? "Sending…" : "Reset password"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
